import { format, isSameDay } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import styled from 'styled-components';

import { Scheduler, Text } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import { breakpoints } from '@service-initiation/shared-ui-breakpoints';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  DeliveryMethod,
  availableAppointmentDatesAsyncAtom,
  availableAppointmentTimesAsyncAtom,
  firstAvailableAppointmentAsyncAtom,
  selectedAppointmentAsyncAtom,
  store,
  useDeliveryMethodValue,
  useUpdatedLeadIdValue,
} from '@service-initiation/tech-care-data-access-store';
import {
  type AppointmentDate,
  type SelectedTimeSlot,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import { ErrorPage } from '@service-initiation/tech-care-ui-error-page';
import {
  NavigationButtonsContainer,
  type NavigationButtonsContainerProps,
} from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { StoreNameHeader } from './components/StoreNameHeader';
import {
  formatHour,
  formatTime,
  getFormattedDate,
  getTodaysScheduleFormatDate,
  isDisabledAppointment,
} from './utils';

const Container = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  gap: 18px;
`;

const StoreNameContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;

  ${breakpoints.tablet} {
    margin-bottom: 16px;
  }
`;

const TEXTS = TranslationEntries.Repairs.AppointmentTime;

export function AppointmentTimePage() {
  const { t } = useTranslation();
  const [, goToNextStep] = useGoToNextStep();

  const { value: selectedAppointment } = useAsyncAtom(
    selectedAppointmentAsyncAtom
  );
  const updatedLeadId = useUpdatedLeadIdValue();
  const { value: firstAppointment } = useAsyncAtom(
    firstAvailableAppointmentAsyncAtom
  );

  const deliveryMethod = useDeliveryMethodValue();
  const isRemoteTechMethod = deliveryMethod === DeliveryMethod.ComeToMe;

  const navigateButtonProps: NavigationButtonsContainerProps = {
    pageName: 'AppointmentTime',
    showBackButton: true,
  };
  //TODO: bandaid solution for July 4th, remove this after the holiday
  const isJuly4th = (date: Date | string) => {
    const appointmentDate = new Date(date);
    return appointmentDate.getMonth() === 6 && appointmentDate.getDate() === 4;
  };

  const { ref: elementRef } = useInViewAnalytics(
    'AppointmentTimePage',
    firstAppointment && {
      FirstDate: firstAppointment?.date,
      FirstTimeSlot: firstAppointment?.availableTimes?.[0]?.start,
    }
  );
  const { dispatchClickEvent } = useAnalytics();

  const {
    error: errorAvailableAppointmentDates,
    loading: loadingAvailableAppointmentDates,
    value: availableAppointmentDates,
  } = useAsyncAtom(availableAppointmentDatesAsyncAtom);

  const {
    error: errorAvailableAppointmentTimes,
    value: availableAppointmentTimes,
  } = useAsyncAtom(availableAppointmentTimesAsyncAtom);

  const selectDate = useCallback(
    (appointment: AppointmentDate) => {
      dispatchClickEvent('ChangeDate', {
        Cta: appointment.date,
      });

      store.set(selectedAppointmentAsyncAtom, {
        date: appointment.date,
      });
    },
    [dispatchClickEvent]
  );

  const [
    { error: errorOnChange, loading: loadingOnChange },
    handleTimeSelection,
  ] = useAsyncFn(
    async (slot: SelectedTimeSlot | undefined) => {
      const isAsapSlot = slot === 'ASAP';
      dispatchClickEvent('SelectHour', {
        Cta: !isAsapSlot ? slot?.start : 'ASAP',
      });

      if (slot === 'ASAP') {
        await store.set(selectedAppointmentAsyncAtom, {
          isAsap: true,
        });
      } else {
        if (!selectedAppointment?.date) {
          throw new Error('Attempting to set appointment time without a date');
        }

        await store.set(selectedAppointmentAsyncAtom, {
          date: selectedAppointment.date,
          endTime: slot?.end,
          startTime: slot?.start,
        });
      }

      goToNextStep();
    },
    [dispatchClickEvent, goToNextStep, selectedAppointment]
  );

  return errorOnChange ||
    errorAvailableAppointmentDates ||
    errorAvailableAppointmentTimes ||
    (!loadingAvailableAppointmentDates &&
      (!availableAppointmentDates || availableAppointmentDates.length <= 0)) ? (
    <ErrorPage
      actionLabel="Try Again"
      link={{ href: window.location.href }}
      subtitle="Please try again."
      title="We seem to be having some difficulties."
    />
  ) : (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          {!loadingOnChange && !loadingAvailableAppointmentDates ? (
            <Container
              data-testid="appointment-time-root"
              id="AppointmentTime"
              ref={elementRef}
            >
              <Scheduler>
                <Scheduler.Heading>
                  {isRemoteTechMethod ? (
                    <StepTitle title={t(TEXTS.RemoteTechTitle)} />
                  ) : (
                    <>
                      <StoreNameContainer>
                        <StoreNameHeader />
                      </StoreNameContainer>
                      <StepTitle
                        style={{ marginTop: 0 }}
                        title={
                          updatedLeadId
                            ? t(TEXTS.RescheduleTitle)
                            : t(TEXTS.Title)
                        }
                      />
                    </>
                  )}
                </Scheduler.Heading>

                <Scheduler.Heading>
                  <Text size={3}>{t(TEXTS.ChooseDate)}</Text>
                </Scheduler.Heading>

                <Scheduler.Carousel aria-label="Appointment time date picker">
                  {availableAppointmentDates?.map((appointment, i) => {
                    const isToday = isSameDay(
                      new Date(),
                      new Date(appointment.date)
                    );
                    const isSelected =
                      appointment.date === selectedAppointment?.date;
                    const ariaLabel = format(
                      new Date(appointment.date),
                      'EEEE MMMM do'
                    );

                    const { day, month, weekday } = getFormattedDate(
                      new Date(appointment.date)
                    );

                    return (
                      <Scheduler.DateCard
                        aria-checked={isSelected}
                        aria-label={ariaLabel}
                        checked={isSelected}
                        data-testid={`appointment-time-date-picker-card-${i}`}
                        date={day}
                        key={`${day}_${month}`}
                        month={month}
                        onClick={() => selectDate(appointment)}
                        tag={
                          (isToday && 'Today') ||
                          ((isDisabledAppointment(appointment) ||
                            isJuly4th(appointment.date || '')) &&
                            'Closed') ||
                          ''
                        }
                        value={appointment.date}
                        weekday={weekday}
                      />
                    );
                  })}
                </Scheduler.Carousel>

                <Scheduler.Heading>
                  {isRemoteTechMethod
                    ? t(TEXTS.ChooseTimeRangeRemoteTech)
                    : t(TEXTS.ChooseTimeRange)}{' '}
                  <Text size={3} weight="heavy">
                    {formatHour(selectedAppointment?.date)}
                  </Text>
                </Scheduler.Heading>

                {availableAppointmentTimes &&
                availableAppointmentTimes.length > 0 &&
                !isJuly4th(selectedAppointment?.date || '') ? (
                  <Scheduler.TimeContainer
                    aria-label="Appointment time time container"
                    data-testid="appointment-time-time-container"
                  >
                    {!isRemoteTechMethod &&
                      selectedAppointment &&
                      selectedAppointment.date ===
                        getTodaysScheduleFormatDate() && (
                        <Scheduler.TimeSlot
                          aria-checked={selectedAppointment?.isAsap}
                          data-testid={`appointment-time-choose-time-card-asap`}
                          disabled={loadingOnChange}
                          onClick={async () =>
                            await handleTimeSelection('ASAP')
                          }
                          role="radio"
                          time={t(TEXTS.ASAPText)}
                          type="selection"
                        />
                      )}
                    {availableAppointmentTimes.map((slot, i) => {
                      const isSelected =
                        !selectedAppointment?.isAsap &&
                        slot.start === selectedAppointment?.startTime &&
                        slot.end === selectedAppointment?.endTime;

                      return (
                        <Scheduler.TimeSlot
                          aria-checked={isSelected}
                          data-testid={`appointment-time-choose-time-card-${i}`}
                          disabled={loadingOnChange}
                          key={`${slot.start}_${slot.end}`}
                          onClick={async () => await handleTimeSelection(slot)}
                          time={formatTime(slot) || ''}
                          value={slot.start}
                        />
                      );
                    })}
                  </Scheduler.TimeContainer>
                ) : (
                  <Scheduler.NoTimesAvailable
                    aria-label="Appointment time no times available container"
                    data-testid="appointment-time-no-times-available-container"
                  >
                    {selectedAppointment?.date && (
                      <Scheduler.NoTimesAvailableHeading>
                        {t(TEXTS.StoreIsClosedText)}
                        {(() => {
                          const { day, month, weekday } = getFormattedDate(
                            new Date(selectedAppointment.date)
                          );
                          return (
                            <>
                              {weekday}, {month} {day}.
                            </>
                          );
                        })()}
                      </Scheduler.NoTimesAvailableHeading>
                    )}
                    <Scheduler.NoTimesAvailableSubheading>
                      {t(TEXTS.StoreIsClosedSubText)}
                    </Scheduler.NoTimesAvailableSubheading>
                  </Scheduler.NoTimesAvailable>
                )}
              </Scheduler>
            </Container>
          ) : (
            <StepContentSkeleton />
          )}
        </LeftMainContentSection>
        <NavigationButtonsContainer {...navigateButtonProps} />
      </MainContentContainer>
    </PageContainer>
  );
}
